// Import: Shapefile (.zip) and KMZ → GeoJSON FeatureCollection
// Export: FeatureCollection → Shapefile/KMZ/GeoJSON download
//
// Uses dynamic import() so these heavy libs only load when the user clicks Import/Export,
// keeping the main bundle small.

export async function importFile(file) {
  const name = file.name.toLowerCase();
  if (name.endsWith('.zip') || name.endsWith('.shp')) {
    return importShapefile(file);
  }
  if (name.endsWith('.kmz') || name.endsWith('.kml')) {
    return importKml(file);
  }
  if (name.endsWith('.geojson') || name.endsWith('.json')) {
    const text = await file.text();
    const fc = JSON.parse(text);
    return normalizeFc(fc, name.replace(/\.[^.]+$/, ''));
  }
  throw new Error(`Unsupported file type: ${file.name}`);
}

async function importShapefile(file) {
  const shp = (await import('shpjs')).default;
  const buffer = await file.arrayBuffer();
  const result = await shp(buffer);
  // shpjs returns either a single FC or an array (multi-shp zip)
  const fc = Array.isArray(result)
    ? { type: 'FeatureCollection', features: result.flatMap((f) => f.features || []) }
    : result;
  return normalizeFc(fc, file.name.replace(/\.[^.]+$/, ''));
}

async function importKml(file) {
  const name = file.name.toLowerCase();
  let kmlText;
  if (name.endsWith('.kmz')) {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(await file.arrayBuffer());
    const kmlEntry = Object.keys(zip.files).find((k) => k.toLowerCase().endsWith('.kml'));
    if (!kmlEntry) throw new Error('No .kml entry in KMZ file');
    kmlText = await zip.files[kmlEntry].async('text');
  } else {
    kmlText = await file.text();
  }
  const togeojson = await import('@tmcw/togeojson');
  const dom = new DOMParser().parseFromString(kmlText, 'text/xml');
  const fc = togeojson.kml(dom);
  return normalizeFc(fc, file.name.replace(/\.[^.]+$/, ''));
}

function normalizeFc(fc, sourceName) {
  if (!fc || !fc.features) return { type: 'FeatureCollection', name: sourceName, features: [] };
  return { type: 'FeatureCollection', name: sourceName, features: fc.features };
}

// ─── Export ────────────────────────────────────────────────────
export async function exportFeatures(features, format, baseName = 'export') {
  const fc = { type: 'FeatureCollection', features: (features || []).map((f) => f.feature || f) };
  if (format === 'geojson') {
    return downloadBlob(JSON.stringify(fc, null, 2), `${baseName}.geojson`, 'application/geo+json');
  }
  if (format === 'kml' || format === 'kmz') {
    const tokml = (await import('@maphubs/tokml')).default;
    const kml = tokml(fc, { name: baseName, documentName: baseName });
    if (format === 'kml') {
      return downloadBlob(kml, `${baseName}.kml`, 'application/vnd.google-earth.kml+xml');
    }
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    zip.file('doc.kml', kml);
    const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/vnd.google-earth.kmz' });
    return downloadFile(blob, `${baseName}.kmz`);
  }
  if (format === 'shapefile' || format === 'shp') {
    const shpwrite = (await import('@mapbox/shp-write')).default;
    const zipBuf = await shpwrite.zip(fc);
    const blob = new Blob([zipBuf], { type: 'application/zip' });
    return downloadFile(blob, `${baseName}.zip`);
  }
  throw new Error(`Unknown format: ${format}`);
}

function downloadBlob(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  return downloadFile(blob, filename);
}

function downloadFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename };
}
